import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { FormSection } from '../../../common/components/SectionedForm'
import { api, buildPaginatedUrl } from '../../../common/api'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ServerPage, VpRole } from '../../../app/types'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tooltip } from '@mui/material'
import { usePrivilege, useUrlHelpers } from '../../../common/util/hooks'
import { FeaturePrivileges } from '../../../common/util/featurePrivileges'
import { ColumnDefinition, InfiniteScrollTable } from '../../../common/components/table/Table'
import TableRowActionsMenu from '../../../common/components/table/TableRowActionsMenu'
import { sorterHeader } from '../../../common/components/table/SorterHeader'
import { Sort, useInfiniteScroll } from '../../../common/components/table/useInfiniteScroll'
import { AddButton } from '../../../common/components/AddButton'
import CancelButton from '../../../common/form/CancelButton'
import useVpRoleForm, { formatPrivilegeName, groupPrivileges } from '../useVpRoleForm'
import ErrorAlert from '../../../common/components/ErrorAlert'
import DeletionModal from '../../../common/components/DeletionModal'
import { SearchField } from '../../../common/components/SearchField'
import { customerOriginIdSelector } from '../../../authentication/redux'

export const useRolesSection = (): FormSection<VpRole[]> => {
    const { t } = useTranslation()
    const { createEnterpriseUrl } = useUrlHelpers()
    const customerOriginId = useSelector(customerOriginIdSelector)
    const [entryToDelete, setEntryToDelete] = useState<VpRole | null>(null)
    const [entryToUpdate, setEntryToUpdate] = useState<VpRole | null>(null)
    const { hasAccess: hasWriteAccess, onlyWithAccess: onlyWithWriteAccess } = usePrivilege(FeaturePrivileges.enterpriseAdminMgmt.write)

    const fetchRoles = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<VpRole>> => {
            if (!customerOriginId) return { content: [], totalPages: 0, number: 0, totalElements: 0 }
            const { data } = await api.get(buildPaginatedUrl(`/api/enterprise/${customerOriginId}/vpRoles`, pageParam, sortParam, searchTermParam))
            return data
        },
        [customerOriginId],
    )

    const { sort, setSort, setSearchTerm, rows, error, fetchNextPage, hasNextPage, isLoading, refetch } = useInfiniteScroll<VpRole>({
        queryKey: `admin-management-roles-${customerOriginId || 'n/a'}`,
        initialSort: { field: 'name', order: 'asc' },
        fetchFunction: fetchRoles,
    })

    const onSubmit = useCallback(
        async (formData: any) => {
            const url = createEnterpriseUrl(`vpRoles${entryToUpdate?.id ? `/${entryToUpdate.id}` : ''}`)
            if (entryToUpdate?.id) {
                await api.put(url, formData)
            } else {
                await api.post(url, formData)
            }
            setEntryToUpdate(null)
            await refetch()
        },
        [refetch, entryToUpdate, createEnterpriseUrl],
    )

    const { handleSubmit, form: Form, submitButton: SubmitButton, reset, setValue } = useVpRoleForm({ entry: entryToUpdate, onSubmit })

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: async (record: VpRole) => {
                    reset()
                    setEntryToUpdate(record)
                    setValue('name', record.name)
                    setValue('description', record.description)
                    setValue('privileges', record.privileges || [])
                    return false
                },
                dividerBeneath: true,
                needsWriteAccess: true,
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: VpRole) => {
                    setEntryToDelete(record)
                },
                needsWriteAccess: true,
            },
        ],
        [t, setValue, reset],
    )

    // @ts-ignore
    const columns: ColumnDefinition<VpRole>[] = useMemo(
        () => [
            {
                title: t('admin-management.role-name'),
                key: 'name',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('admin-management.role-description'),
                key: 'description',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('admin-management.role-type'),
                key: 'global',
                sorter: false,
                render: (global: boolean) => (
                    <Chip
                        label={t(global ? 'admin-management.role-type-global' : 'admin-management.role-type-custom')}
                        size='small'
                        color={'default'}
                        variant='outlined'
                    />
                ),
            },
            {
                title: t('admin-management.role-privileges'),
                key: 'privileges',
                sorter: false,
                render: (privileges: string[]) => {
                    const list = privileges || []
                    if (list.length === 0) return <></>
                    const groups = groupPrivileges(list, t)
                    return (
                        <Tooltip
                            title={
                                <>{groups.map(g => (
                                    <div key={g.section} style={{ marginBottom: 4 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{t(`admin-management.privilege-section.${g.section}`)}</div>
                                        {g.privileges.map(p => <div key={p} style={{ paddingLeft: 8 }}>{formatPrivilegeName(p, t)}</div>)}
                                    </div>
                                ))}</>
                            }
                            arrow
                            placement='left'
                        >
                            <Chip label={`${list.length}`} size='small' variant='outlined' />
                        </Tooltip>
                    )
                },
            },
            {
                title: '',
                key: 'id',
                sorter: false,
                render: (_: string, record: VpRole) => !record.global ? <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess} /> : <></>,
            },
        ],
        [t, rowActions, hasWriteAccess],
    )

    const onSearch = useCallback(async (searchTerm: string) => setSearchTerm(searchTerm), [setSearchTerm])
    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])
    const getRowKey = useCallback(entry => entry.id, [])
    const getDeleteUrl = useCallback(i => i ? createEnterpriseUrl(`vpRoles/${i.id}`) : null, [createEnterpriseUrl])

    const buttonPanel = useMemo(
        () => onlyWithWriteAccess(
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                <Grid item>
                    <AddButton
                        onClick={() => {
                            reset()
                            setEntryToUpdate({ id: '', name: '', description: '', customerId: customerOriginId, privileges: [], global: false })
                            return null
                        }}
                        label={`${t('admin-management.create-role')}`}
                    />
                </Grid>
            </Grid>,
        ),
        [t, reset, onlyWithWriteAccess, customerOriginId],
    )

    const entryForm = useMemo(
        () => (
            <form className={'ant-form ant-form-vertical'} onSubmit={handleSubmit} noValidate={true}>
                <Dialog disablePortal open={!!entryToUpdate} onClose={() => setEntryToUpdate(null)} maxWidth='md' fullWidth>
                    <DialogTitle>{t(entryToUpdate?.id ? 'admin-management.edit-role' : 'admin-management.create-role')}</DialogTitle>
                    <DialogContent>
                        <Form />
                    </DialogContent>
                    <DialogActions>
                        <SubmitButton />
                        <CancelButton onClick={() => setEntryToUpdate(null)} />
                    </DialogActions>
                </Dialog>
            </form>
        ),
        [Form, SubmitButton, entryToUpdate, handleSubmit, t],
    )

    const renderDetails = useCallback(
        (data: VpRole[]): ReactNode => {
            return (
                <Grid container p={1}>
                    <Grid container xs={12} mb={3} item justifyContent={'flex-start'} flexDirection={'row'} flexWrap={'nowrap'}>
                        <Grid item>{buttonPanel}</Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <SearchField onSearch={onSearch} />
                    </Grid>
                    <ErrorAlert showAlert={!!error} />
                    <Grid item xs={12}>
                        <InfiniteScrollTable<VpRole>
                            hasNextPage={hasNextPage}
                            data={rows}
                            fetchNextPage={fetchNextPage}
                            isFetching={false}
                            getRowKey={getRowKey}
                            sorter={sorter}
                            columns={columns}
                            tableId={'adminRolesTable'}
                        />
                    </Grid>
                    <DeletionModal<VpRole | null>
                        text={role => role ? t('admin-management.role-delete-warning', { name: role.name }) : ''}
                        nameToDelete={role => role?.name || ''}
                        itemToDelete={entryToDelete} deleteUrl={getDeleteUrl} onClose={() => setEntryToDelete(null)} onSuccess={refetch} />
                    {entryForm}
                </Grid>
            )
        },
        [columns, buttonPanel, entryForm, entryToDelete, error, fetchNextPage, getDeleteUrl, getRowKey, hasNextPage, onSearch, refetch, rows, sorter, t],
    )

    return {
        title: t('admin-management.roles-section'),
        data: rows,
        isFetching: isLoading || false,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: null,
        enabled: () => true,
        icon: () => <AdminPanelSettingsIcon />,
    }
}
